import React, { useEffect, useState, useRef } from "react";

import axios from "axios";

import printJS from "print-js";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { TabMenu } from "primereact/tabmenu";
import { Toast } from "primereact/toast";

import { currencyAmount } from "../../../helpers/TemplateHelper";

function OrderDetailsSummaryReport() {
  const items = [
    { label: "Order Details", icon: "pi pi-list" },
    { label: "Order Summary", icon: "pi pi-chart-line" },
  ];

  const printStyles = `
    body {font-family: Roboto, Helvetica Neue Light, Helvetica Neue, Helvetica, Arial, Lucida Grande, sans-serif; }
    label, div {font-size: 11px}
    strong {font-size: 10px}
    h4 {font-size: 13px}
    table, td, th {border: 0.5px groove ; font-size: 11px; padding: 4px} 
    table {width: 100%; border-collapse: collapse; margin-bottom: 10px}
    th {font-weight: 600; text-align: left, padding: 4px}
    .updated-by {width: 50px !important}
    .created {width: 40px !important}
    .totalpole {width: 20px !important}
    .transportationcost {width: 10px !important}
    .distance {width: 10px !important}
    .month {width: 30px !important}
    .customername {width: 10px !important}
    .text-align-right {text-align: right}
    .text-align-left {text-align: left}
    .border-none td {border: none; font-size: 11px; padding: 1px}
    .text-align-center {text-align: center}`;

  const toast = useRef(null);

  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [orders, setOrders] = useState([]);

  const [toDate, setToDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );

  const [activeTab, setActiveTab] = useState(0); // 0: Order Details, 1: Order Summary

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [summaryReportMonth, setsummaryReportMonth] = useState(null);

  const [selectedQuarter, setSelectedQuarter] = useState(null);

  const [summaryType, setSummaryType] = useState("Monthly");

  const tabItems = [
    { label: "Order Details", icon: "pi pi-list" },
    { label: "Order Summary", icon: "pi pi-chart-line" },
  ];

  const [monthlySummaryOrders, setMonthlySummaryOrders] = useState([]);

  const quarterOptions = [
    { label: "Q1 (Jan - Mar)", value: "Q1" },
    { label: "Q2 (Apr - Jun)", value: "Q2" },
    { label: "Q3 (Jul - Sep)", value: "Q3" },
    { label: "Q4 (Oct - Dec)", value: "Q4" },
  ];

  const yearOptions = Array.from({ length: 20 }, (_, i) => ({
    label: `${new Date().getFullYear() - i}`,
    value: new Date().getFullYear() - i,
  }));

  const currentDate = new Date();

  const exportOrderDetailsCSV = (filename) => {
    const table = document.querySelector("#orderSummary");
    if (!table) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "No table data to export",
        life: 3000,
      });
      return;
    }

    let csv = [];
    const rows = table.querySelectorAll("tr");

    rows.forEach((row) => {
      const cols = row.querySelectorAll("td, th");
      const rowData = [];
      cols.forEach((col) => {
        rowData.push(`"${col.innerText}"`);
      });
      csv.push(rowData.join(","));
    });

    const csvFile = new Blob([csv.join("\n")], { type: "text/csv" });
    const downloadLink = document.createElement("a");
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const exportMonthlyOrderSummaryCSV = (filename) => {
    const table = document.querySelector("#monthlyOrderSummary");
    if (!table) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "No table data to export",
        life: 3000,
      });
      return;
    }

    let csv = [];
    const rows = table.querySelectorAll("tr");

    rows.forEach((row) => {
      const cols = row.querySelectorAll("td, th");
      const rowData = [];
      cols.forEach((col) => {
        rowData.push(`"${col.innerText}"`);
      });
      csv.push(rowData.join(","));
    });

    const csvFile = new Blob([csv.join("\n")], { type: "text/csv" });
    const downloadLink = document.createElement("a");
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  function decodeJwt(token) {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT format");
    }

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  }

  useEffect(() => {
    fetchData();

    const token = localStorage.getItem("token");

    if (token) {
      const decodedPayload = decodeJwt(token);
      const firstName = decodedPayload.firstName;
      const lastName = decodedPayload.lastName;
      setUserFirstName(firstName);
      setUserLastName(lastName);
    }
  }, []);

  const fetchData = () => {
    if (fromDate && toDate) {
      const from = fromDate?.toISOString();
      const to = toDate?.toISOString();

      axios
        .get(
          `/api/reports/datewise-order-details-report?fromDate=${from}&toDate=${to}`
        )
        .then((res) => {
          const ordersWithAddonCost = res.data.map((order) => {
            const addonCost = calculateAddonCost(order.addOns);
            const discountTotal = calculateDiscountTotal(order.addOns);
            const totalAddonCostWithDiscount = addonCost - discountTotal;
            return { ...order, discountTotal, totalAddonCostWithDiscount };
          });
          setOrders(ordersWithAddonCost);
        })
        .catch((error) => {
          console.log(error);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "An error occurred while processing your request.",
            life: 3000,
          });
        });
    } else {
      toast.current.show({
        severity: "warn",
        summary: "Error",
        detail: "Please select date range",
        life: 3000,
      });
    }
  };

  const fetchMonthlySummaryData = () => {
    if (selectedMonth) {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;

      axios
        .get(
          `/api/reports/monthly-order-summary-report?year=${year}&month=${month}`
        )
        .then((res) => {
          const ordersWithAddonCost = res.data.map((order) => {
            const addonCost = calculateAddonCost(order.addOns);
            const discountTotal = calculateDiscountTotal(order.addOns);
            const totalAddonCostWithDiscount = addonCost - discountTotal;
            return { ...order, discountTotal, totalAddonCostWithDiscount };
          });
          setMonthlySummaryOrders(ordersWithAddonCost);
          setsummaryReportMonth(selectedMonth);
        })
        .catch((error) => {
          console.log(error);
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "An error occurred while processing your request.",
            life: 3000,
          });
        });
    } else {
      toast.current.show({
        severity: "warn",
        summary: "Error",
        detail: "Please select a month",
        life: 3000,
      });
    }
  };

  const calculateAddonCost = (addOns) => {
    let addonCost = 0;

    if (addOns) {
      addOns.forEach((addOn) => {
        addonCost += addOn.amount;
      });
    }

    return addonCost;
  };

  const calculateDiscountTotal = (addOns) => {
    let discountTotal = 0;

    if (addOns) {
      addOns.forEach((addOn) => {
        if (addOn.type === "discounts") {
          discountTotal += addOn.amount;
        }
      });
    }

    return discountTotal;
  };

  const calculateTotalAmount = (order) => {
    const {
      orderDetails: { subTotal, transportationCost, fixingCost },
      totalAddonCostWithDiscount,
    } = order;

    return (
      subTotal + transportationCost + fixingCost + totalAddonCostWithDiscount
    );
  };

  const calculateTotalPayments = (order) => {
    if (order && order.payments && order.payments.length > 0) {
      const totalReceivedPayment = order.payments.reduce(
        (total, payment) => total + payment.amount,
        0
      );
      return totalReceivedPayment;
    }
    return 0;
  };

  const calculateRemainingPayment = (order) => {
    const totalAmount = calculateTotalAmount(order);
    const { discountTotal } = order;
    const totalPayments = calculateTotalPayments(order);

    const remainingPayment = totalAmount - (discountTotal + totalPayments);
    return remainingPayment > 0 ? remainingPayment : 0;
  };

  const formatDate = (dateString) => {
    const options = { day: "2-digit", month: "2-digit", year: "2-digit" };
    return new Date(dateString).toLocaleDateString("en-GB", options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formattedDate = `${formatDate(currentDate)} - ${formatTime(
    currentDate
  )}`;

  function formatTableDate(dateString) {
    const options = {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
    };

    return new Date(dateString).toLocaleDateString("en-US", options);
  }

  const handlePrint = () => {
    printJS({
      printable: "order-details-report",
      type: "html",
      scanStyles: false,
      style: printStyles,
    });
  };

  const handleMonthlyPrint = () => {
    printJS({
      printable: "monthly-order-summary-report",
      type: "html",
      scanStyles: false,
      style: printStyles,
    });
  };

  const getOrderDetailsReportTotal = () => {
    let totalSets = 0;
    let totalPleats = 0;
    let totalPoleWidth = 0;
    let totalDistanceKM = 0;
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalAddons = 0;
    let totalFixingCost = 0;
    let totalTransportationCost = 0;
    let totalPayment = 0;
    let totalBalance = 0;

    if (orders && orders.length > 0) {
      orders.forEach((record) => {
        totalSets += parseInt(record?.orderDetails?.totalQty) || 0;
        totalPleats += parseInt(record?.orderDetails?.totalPleats) || 0;
        totalPoleWidth += parseInt(record?.orderDetails?.totalWidth) || 0;
        totalDistanceKM += parseInt(record?.orderDetails?.distance) || 0;
        totalAmount += parseFloat(calculateTotalAmount(record)) || 0;
        totalDiscount += parseFloat(record?.discountTotal) || 0;
        totalAddons += parseFloat(record?.totalAddonCostWithDiscount) || 0;
        totalFixingCost += parseFloat(record?.orderDetails?.fixingCost) || 0;
        totalTransportationCost +=
          parseFloat(record?.orderDetails?.transportationCost) || 0;
        totalPayment += parseFloat(calculateTotalPayments(record)) || 0;
        totalBalance += parseFloat(calculateRemainingPayment(record)) || 0;
      });
    }

    return {
      totalSets,
      totalPleats,
      totalPoleWidth,
      totalDistanceKM,
      totalAmount,
      totalDiscount,
      totalAddons,
      totalFixingCost,
      totalTransportationCost,
      totalPayment,
      totalBalance,
    };
  };

  const {
    totalSets,
    totalPleats,
    totalPoleWidth,
    totalDistanceKM,
    totalAmount,
    totalDiscount,
    totalAddons,
    totalFixingCost,
    totalTransportationCost,
    totalPayment,
    totalBalance,
  } = getOrderDetailsReportTotal();

  const getMonthlyOrderSummaryReportTotal = () => {
    let monthlyTotalSets = 0;
    let monthlyTotalPleats = 0;
    let monthlyTotalPoleWidth = 0;
    let monthlyTotalDistanceKM = 0;
    let monthlyTotalAmount = 0;
    let monthlyTotalDiscount = 0;
    let monthlyTotalAddons = 0;
    let monthlyTotalFixingCost = 0;
    let monthlyTotalTransportationCost = 0;
    let monthlyTotalPayment = 0;
    let monthlyTotalBalance = 0;

    if (monthlySummaryOrders && monthlySummaryOrders.length > 0) {
      monthlySummaryOrders.forEach((record) => {
        monthlyTotalSets += parseInt(record?.orderDetails?.totalQty) || 0;
        monthlyTotalPleats += parseInt(record?.orderDetails?.totalPleats) || 0;
        monthlyTotalPoleWidth +=
          parseInt(record?.orderDetails?.totalWidth) || 0;
        monthlyTotalDistanceKM += parseInt(record?.orderDetails?.distance) || 0;

        monthlyTotalAmount += parseFloat(calculateTotalAmount(record)) || 0;
        monthlyTotalDiscount += parseFloat(record?.discountTotal) || 0;
        monthlyTotalAddons +=
          parseFloat(record?.totalAddonCostWithDiscount) || 0;
        monthlyTotalFixingCost +=
          parseFloat(record?.orderDetails?.fixingCost) || 0;
        monthlyTotalTransportationCost +=
          parseFloat(record?.orderDetails?.transportationCost) || 0;
        monthlyTotalPayment += parseFloat(calculateTotalPayments(record)) || 0;
        monthlyTotalBalance +=
          parseFloat(calculateRemainingPayment(record)) || 0;
      });
    }

    return {
      monthlyTotalSets,
      monthlyTotalPleats,
      monthlyTotalPoleWidth,
      monthlyTotalDistanceKM,
      monthlyTotalAmount,
      monthlyTotalDiscount,
      monthlyTotalAddons,
      monthlyTotalFixingCost,
      monthlyTotalTransportationCost,
      monthlyTotalPayment,
      monthlyTotalBalance,
    };
  };

  const {
    monthlyTotalSets,
    monthlyTotalPleats,
    monthlyTotalPoleWidth,
    monthlyTotalDistanceKM,
    monthlyTotalAmount,
    monthlyTotalDiscount,
    monthlyTotalAddons,
    monthlyTotalFixingCost,
    monthlyTotalTransportationCost,
    monthlyTotalPayment,
    monthlyTotalBalance,
  } = getMonthlyOrderSummaryReportTotal();

  return (
    <div className="container">
      <Toast ref={toast} position="bottom-right" />
      <div className="card mb-3">
        <TabMenu
          model={tabItems}
          activeIndex={activeTab}
          onTabChange={(e) => setActiveTab(e.index)}
          style={{ fontSize: "15px" }}
        />
      </div>

      {/* === Order Details Tab === */}
      {activeTab === 0 && (
        <div className="flex flex-wrap justify-between align-items-center mb-3 w-full">
          <div className="flex-grow-1">
            <h5 className="text-primary mb-1">
              Order Details Report - Datewise
            </h5>
            <label className="text-secondary">
              View datewise order details report and print
            </label>
          </div>
          <div className="flex gap-2 align-items-center flex-wrap justify-end">
            <span className="filter-label">From Date : </span>
            <Calendar
              value={fromDate}
              onChange={(e) => setFromDate(e.value)}
              showButtonBar
              dateFormat="yy-mm-dd"
            />
            <span className="filter-label">To Date : </span>
            <Calendar
              value={toDate}
              onChange={(e) => setToDate(e.value)}
              showButtonBar
              dateFormat="yy-mm-dd"
            />
            <Button
              style={{ height: "33px" }}
              label="Refresh Data"
              severity="warning"
              onClick={() => fetchData()}
            />
            <Button
              label="Print Report"
              icon="pi pi-print"
              onClick={handlePrint}
            />
            <Button
              label="Export"
              icon="pi pi-file"
              severity="success"
              onClick={() => exportOrderDetailsCSV("order-details-report.csv")}
            />
          </div>

          {orders && fromDate && toDate && orders.length > 0 && (
            <div
              id="order-details-report"
              style={{
                fontSize: "11px !important",
                marginTop: "8px",
                width: "100%",
              }}
            >
              <div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                      gap: "200px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div className="report-header">
                        <h4>Order Details Report - Datewise</h4>
                      </div>
                      <div style={{ paddingTop: "15px" }}>
                        <div>Created: {formattedDate}</div>
                        <div>
                          Created By: {userFirstName} {""} {userLastName}{" "}
                        </div>
                        <div>
                          From {fromDate && formatDate(fromDate)} to{" "}
                          {toDate && formatDate(toDate)}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          paddingTop: "18px",
                        }}
                      >
                        <img
                          src={require("../../../images/logo.png")}
                          alt="logo"
                          style={{ height: "50px" }}
                        />
                      </div>
                      <div
                        className="text-align-right text-right"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <label>
                          RAGAMA - KURUNEGALA - WENNAPPUWA - WELIMADA - WALPOLA
                        </label>
                        <label>Email : geethcurtains@gmail.com</label>
                        <label>Phone : 0718021976 - 0771786299</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                id="orderSummary"
                className="flex flex-column flex-wrap summary"
                style={{
                  marginTop: "10px",
                }}
              >
                <table className="order-table" style={{ minWidth: "950px" }}>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th className="created" style={{ width: "50px" }}>
                        Created
                      </th>
                      <th style={{ width: "15px" }}>Order Id</th>
                      <th style={{ width: "15px" }}>Bill No</th>
                      <th style={{ width: "15px" }}>JON</th>
                      <th className="created" style={{ width: "10px" }}>
                        Delivery Date
                      </th>

                      <th className="customername" style={{ width: "20px" }}>
                        Customer Name
                      </th>

                      <th className="totalpole" style={{ width: "20px" }}>
                        Sets
                      </th>
                      <th className="totalpole" style={{ width: "20px" }}>
                        Total Pleats
                      </th>
                      <th className="totalpole" style={{ width: "40px" }}>
                        Total Pole Length (F)
                      </th>
                      <th className="distance" style={{ width: "15px" }}>
                        Distance (km)s
                      </th>
                      <th>Amount</th>
                      <th>Discount</th>
                      <th>Addons</th>
                      <th>Fixing Cost</th>
                      <th
                        className="transportationcost"
                        style={{ width: "20px" }}
                      >
                        Transportation Cost
                      </th>
                      <th>Payment</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((record, index) => (
                      <tr key={index}>
                        <td className="text-align-center">{index + 1}</td>
                        <td className="text-align-center">
                          {formatTableDate(record?.created)}
                        </td>

                        <td className="text-align-center">
                          {record?.id.toString().padStart(8, "0")}
                        </td>
                        <td className="text-align-center">
                          {record?.payments[0]?.referenceNo}
                        </td>
                        <td className="text-align-center">{record?.jobOrderNo}</td>
                        <td className="text-align-center">{record?.deliveryDate ? formatTableDate(record.deliveryDate) : ""}</td>

                        <td className="text-align-center">{`${
                          record?.customerDetails?.firstName || ""
                        } ${record?.customerDetails?.lastName || ""}`}</td>

                        <td className="text-align-center text-center">
                          {record?.orderDetails?.totalQty}
                        </td>
                        <td className="text-align-center text-center">
                          {record?.orderDetails?.totalPleats}
                        </td>
                        <td className="text-align-center text-center">
                          {record?.orderDetails?.totalWidth}
                        </td>
                        <td className="text-align-center text-center">
                          {record?.orderDetails?.distance}
                        </td>
                        <td className="text-align-right text-right">
                          {`${currencyAmount(
                            calculateTotalAmount(record)
                          )} LKR`}
                        </td>
                        <td className="text-align-right text-right">
                          {`${currencyAmount(record?.discountTotal)} LKR`}
                        </td>
                        <td className="text-align-right text-right">
                          {`${currencyAmount(
                            record?.totalAddonCostWithDiscount
                          )} LKR`}
                        </td>
                        <td className="text-align-right text-right">
                          {`${currencyAmount(
                            record?.orderDetails?.fixingCost
                          )} LKR`}
                        </td>
                        <td className="text-align-right text-right">
                          {`${currencyAmount(
                            record?.orderDetails?.transportationCost
                          )} LKR`}
                        </td>
                        <td className="text-align-right text-right">
                          {`${currencyAmount(
                            calculateTotalPayments(record)
                          )} LKR`}
                        </td>
                        <td className="text-align-right text-right">
                          {`${currencyAmount(
                            calculateRemainingPayment(record)
                          )} LKR`}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="7" className="text-align-right text-right">
                        <strong>Total</strong>
                      </td>
                      <td className="text-align-center text-center">
                        <strong>{totalSets}</strong>
                      </td>
                      <td className="text-align-center text-center">
                        <strong>{totalPleats}</strong>
                      </td>
                      <td className="text-align-center text-center">
                        <strong>{totalPoleWidth}</strong>
                      </td>
                      <td className="text-align-center text-center">
                        <strong>{totalDistanceKM}</strong>
                      </td>
                      <td className="text-align-right text-right">
                        <strong>{`${currencyAmount(totalAmount)} LKR`}</strong>
                      </td>
                      <td className="text-align-right text-right">
                        <strong>{`${currencyAmount(
                          totalDiscount
                        )} LKR`}</strong>
                      </td>
                      <td className="text-align-right text-right">
                        <strong>{`${currencyAmount(totalAddons)} LKR`}</strong>
                      </td>
                      <td className="text-align-right text-right">
                        <strong>{`${currencyAmount(
                          totalFixingCost
                        )} LKR`}</strong>
                      </td>
                      <td className="text-align-right text-right">
                        <strong>{`${currencyAmount(
                          totalTransportationCost
                        )} LKR`}</strong>
                      </td>
                      <td className="text-align-right text-right">
                        <strong>{`${currencyAmount(totalPayment)} LKR`}</strong>
                      </td>
                      <td className="text-align-right text-right">
                        <strong>{`${currencyAmount(totalBalance)} LKR`}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === Order Summary Tab === */}
      {activeTab === 1 && (
        <div className="flex justify-between align-items-center flex-wrap gap-3 mt-3">
          <div style={{ minWidth: "200px" }}>
            <h5 className="text-primary mb-1">
              Order Summary Report - {summaryType}
            </h5>
            <label className="text-secondary">
              View {summaryType} order summary report and print
            </label>
          </div>

          {/* Report Type */}
          <div className="flex-grow-1 flex justify-center ml-6">
            <div className="card shadow-2 p-3" style={{ minWidth: "270px" }}>
              <label className="font-bold block mb-2">
                Select Report Type:
              </label>
              <div className="flex gap-4 align-items-center flex-wrap">
                {["Monthly"].map((type) => (
                  <div key={type} className="flex align-items-center gap-2">
                    <RadioButton
                      inputId={type}
                      name="summaryType"
                      value={type}
                      onChange={(e) => setSummaryType(e.value)}
                      checked={summaryType === type}
                    />
                    <label htmlFor={type}>{type}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex align-items-center gap-2 flex-wrap justify-content-end flex-grow-1">
            {/* Monthly */}
            {summaryType === "Monthly" && (
              <>
                <label>Month:</label>
                <Calendar
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.value)}
                  view="month"
                  dateFormat="yy/mm"
                />
                <Button
                  style={{ height: "33px" }}
                  label="Refresh Data"
                  severity="warning"
                  onClick={() => fetchMonthlySummaryData()}
                />
                <Button
                  label="Print Report"
                  icon="pi pi-print"
                  onClick={handleMonthlyPrint}
                />
                <Button
                  label="Export"
                  icon="pi pi-file"
                  severity="success"
                  onClick={() =>
                    exportMonthlyOrderSummaryCSV("monthly-order-summary-report.csv")
                  }
                />
              </>
            )}

            {/* Quarterly */}
            {summaryType === "Quarterly" && (
              <>
                <label>Year:</label>
                <Dropdown
                  value={selectedYear}
                  options={yearOptions}
                  onChange={(e) => setSelectedYear(e.value)}
                  placeholder="Select Year"
                />
                <label>Quarter:</label>
                <Dropdown
                  value={selectedQuarter}
                  options={quarterOptions}
                  onChange={(e) => setSelectedQuarter(e.value)}
                  placeholder="Select Quarter"
                />
                <Button
                  style={{ height: "33px" }}
                  label="Refresh Data"
                  severity="warning"
                  //onClick={() => fetchQuarterlySummaryData()}
                />
                <Button
                  label="Print Report"
                  icon="pi pi-print"
                  //onClick={handleQuarterlyPrint}
                />
              </>
            )}

            {/* Annually */}
            {summaryType === "Annually" && (
              <>
                <label>Year:</label>
                <Dropdown
                  value={selectedYear}
                  options={yearOptions}
                  onChange={(e) => setSelectedYear(e.value)}
                  placeholder="Select Year"
                />
                <Button
                  style={{ height: "33px" }}
                  label="Refresh Data"
                  severity="warning"
                  //onClick={() => fetchAnnuallySummaryData()}
                />
                <Button
                  label="Print Report"
                  icon="pi pi-print"
                  //onClick={handleAnnuallyPrint}
                />
              </>
            )}
          </div>

          {/* Monthly Summary Report */}
          {summaryType === "Monthly" &&
            monthlySummaryOrders &&
            selectedMonth &&
            monthlySummaryOrders.length > 0 && (
              <div
                id="monthly-order-summary-report"
                style={{ fontSize: "13px", marginTop: "8px", width: "100%" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "30px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <h4 className="report-header">
                      Order Summary Report - Monthly
                    </h4>
                    <div style={{ paddingTop: "15px" }}>
                      <div>Created: {formattedDate}</div>
                      <div>
                        Created By: {userFirstName} {userLastName}
                      </div>
                      <div>
                        Month:{" "}
                        {summaryReportMonth?.toLocaleString("default", {
                          month: "short",
                        })}{" "}
                        - {summaryReportMonth?.getFullYear()}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <img
                      src={require("../../../images/logo.png")}
                      alt="logo"
                      style={{ height: "50px", marginBottom: "5px" }}
                    />
                    <div
                      className="text-align-right text-right"
                      style={{ fontSize: "13px" }}
                    >
                      <label>
                        RAGAMA - KURUNEGALA - WENNAPPUWA - WELIMADA - WALPOLA
                      </label>
                      <br />
                      <label>Email: geethcurtains@gmail.com</label>
                      <br />
                      <label>Phone: 0718021976 - 0771786299</label>
                    </div>
                  </div>
                </div>

                <div
                  id="monthlyOrderSummary"
                  className="flex flex-column flex-wrap summary"
                  style={{ marginTop: "10px" }}
                >
                  <table
                    className="monthly-order-summary-table"
                    style={{ minWidth: "950px" }}
                  >
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Sets</th>
                        <th>Total Pleats</th>
                        <th>Total Pole Length (F)</th>
                        <th>Distance (km)s</th>
                        <th>Amount</th>
                        <th>Discount</th>
                        <th>Addons</th>
                        <th>Fixing Cost</th>
                        <th>Transportation Cost</th>
                        <th>Payment</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="text-align-center text-center">
                          {summaryReportMonth?.toLocaleString("default", {
                            month: "short",
                          })}{" "}
                          - {summaryReportMonth?.getFullYear()}
                        </td>
                        <td className="text-align-center text-center">
                          {monthlyTotalSets}
                        </td>
                        <td className="text-align-center text-center">
                          {monthlyTotalPleats}
                        </td>
                        <td className="text-align-center text-center">
                          {monthlyTotalPoleWidth}
                        </td>
                        <td className="text-align-center text-center">
                          {monthlyTotalDistanceKM}
                        </td>
                        <td className="text-align-right text-right">{`${currencyAmount(
                          monthlyTotalAmount
                        )} LKR`}</td>
                        <td className="text-align-right text-right">{`${currencyAmount(
                          monthlyTotalDiscount
                        )} LKR`}</td>
                        <td className="text-align-right text-right">{`${currencyAmount(
                          monthlyTotalAddons
                        )} LKR`}</td>
                        <td className="text-align-right text-right">{`${currencyAmount(
                          monthlyTotalFixingCost
                        )} LKR`}</td>
                        <td className="text-align-right text-right">{`${currencyAmount(
                          monthlyTotalTransportationCost
                        )} LKR`}</td>
                        <td className="text-align-right text-right">{`${currencyAmount(
                          monthlyTotalPayment
                        )} LKR`}</td>
                        <td className="text-align-right text-right">{`${currencyAmount(
                          monthlyTotalBalance
                        )} LKR`}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default OrderDetailsSummaryReport;
